import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import { motion } from 'framer-motion';
import ReactMarkdown from 'react-markdown';
import { 
  ArrowLeft, Dog, MapPin, ChevronRight, Calendar,
  BookOpen, Search, Loader2, Briefcase
} from 'lucide-react';
import { Button } from '@/components/ui/button';

import { API } from '@/utils/api';
import { useTranslation } from 'react-i18next';
const BASE_URL = 'https://apisai.pl';

// Blog article page
export default function BlogArticle() {
  const { t } = useTranslation();
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [relatedKennels, setRelatedKennels] = useState([]);
  const [relatedLitters, setRelatedLitters] = useState([]);
  const [relatedArticles, setRelatedArticles] = useState([]);
  const [relatedCatalog, setRelatedCatalog] = useState([]);
  const [seo, setSeo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchArticle = async () => {
      try {
        const res = await axios.get(`${API}/blog/article/${slug}`);
        setArticle(res.data.article);
        setRelatedKennels(res.data.related_kennels || []);
        setRelatedLitters(res.data.related_litters || []);
        setRelatedArticles(res.data.related_articles || []);
        setRelatedCatalog(res.data.related_catalog || []);
        setSeo(res.data.seo);
      } catch (e) {
        setError('Artykuł nie znaleziony');
      } finally {
        setLoading(false);
      }
    };
    fetchArticle();
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0B0F1A] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-amber-400 animate-spin" />
      </div>
    );
  }

  if (error || !article) {
    return (
      <div className="min-h-screen bg-[#0B0F1A] flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-400 text-xl mb-4">{error}</p>
          <Link to="/blog">
            <Button variant="outline">Wróć do bloga</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0B0F1A]">
      {/* SEO */}
      {seo && (
        <Helmet>
          <title>{seo.title} | ApisAI Blog</title>
          <meta name="description" content={seo.description} />
          <link rel="canonical" href={`${BASE_URL}${seo.canonical}`} />
          <meta property="og:title" content={seo.og?.title || seo.title} />
          <meta property="og:description" content={seo.og?.description || seo.description} />
          <meta property="og:type" content="article" />
          <meta property="og:url" content={seo.og?.url} />
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content={seo.og?.title || seo.title} />
          <meta name="twitter:description" content={seo.og?.description || seo.description} />
          <meta name="robots" content="index, follow" />
          <script type="application/ld+json">
            {JSON.stringify(seo.schema)}
          </script>
          {seo.breadcrumbs && (
            <script type="application/ld+json">
              {JSON.stringify({
                "@context": "https://schema.org",
                "@type": "BreadcrumbList",
                "itemListElement": seo.breadcrumbs.map((b, i) => ({
                  "@type": "ListItem",
                  "position": i + 1,
                  "name": b.name,
                  "item": `${BASE_URL}${b.url}`
                }))
              })}
            </script>
          )}
        </Helmet>
      )}

      {/* Header */}
      <header className="bg-[#0B0F1A] border-b border-white/10 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/blog" className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors">
              <ArrowLeft className="w-5 h-5" />
              <span>Wróć do bloga</span>
            </Link>
            <Link to="/" className="text-amber-400 font-bold">ApisAI</Link>
          </div>
        </div>
      </header>

      {/* Article content */}
      <article className="max-w-4xl mx-auto px-6 py-12">
        {/* Title */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="flex items-center gap-2 text-amber-400 text-sm mb-4">
            <BookOpen className="w-4 h-4" />
            <span>Przewodnik po rasie</span>
          </div>
          <h1 className="text-3xl md:text-5xl font-bold text-white mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
            {article.title}
          </h1>
          <p className="text-xl text-gray-400">
            {article.meta_description}
          </p>
        </motion.div>

        {/* Content */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="max-w-none blog-content"
          style={{ fontFamily: 'Manrope, sans-serif' }}
        >
          <style>{`
            .blog-content > h1:first-child { display: none; }
            .blog-content h1 { font-size: 2rem; font-weight: 700; color: #fff; margin: 2rem 0 1rem; }
            .blog-content h2 { font-size: 1.5rem; font-weight: 700; color: #f59e0b; margin: 2.5rem 0 0.75rem; }
            .blog-content h3 { font-size: 1.25rem; font-weight: 600; color: #e5e7eb; margin: 1.5rem 0 0.5rem; }
            .blog-content p { color: #d1d5db; line-height: 1.8; margin-bottom: 1rem; font-size: 1.05rem; }
            .blog-content ul, .blog-content ol { color: #d1d5db; margin: 0.75rem 0; padding-left: 1.5rem; }
            .blog-content li { color: #d1d5db; line-height: 1.8; margin-bottom: 0.25rem; }
            .blog-content strong { color: #fff; font-weight: 600; }
            .blog-content a { color: #f59e0b; text-decoration: none; }
            .blog-content a:hover { text-decoration: underline; }
            .blog-content hr { border-color: rgba(255,255,255,0.1); margin: 2rem 0; }
            .blog-content blockquote { border-left: 3px solid #f59e0b; padding-left: 1rem; margin: 1rem 0; color: #9ca3af; font-style: italic; }
          `}</style>
          <ReactMarkdown>{article.content.replace(/^#\s+.+\n/m, '')}</ReactMarkdown>
        </motion.div>

        {/* Related kennels (breed articles only) */}
        {relatedKennels.length > 0 && article.breed && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="mt-16 pt-8 border-t border-white/10"
          >
            <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-2">
              <Dog className="w-6 h-6 text-amber-400" />
              Hodowle {article.breed}
            </h2>
            <div className="grid md:grid-cols-2 gap-4">
              {relatedKennels.map((kennel, i) => (
                <Link 
                  key={i}
                  to={`/hodowla/${kennel.slug}`}
                  className="flex items-center gap-4 p-4 bg-slate-800/50 rounded-xl border border-slate-700 hover:border-amber-500/50 transition-colors"
                >
                  {kennel.cover_image && (
                    <img 
                      src={kennel.cover_image} 
                      alt={kennel.name}
                      className="w-16 h-16 rounded-lg object-cover"
                    />
                  )}
                  <div className="flex-1">
                    <p className="text-white font-medium">{kennel.name}</p>
                    {kennel.city && (
                      <p className="text-gray-400 text-sm flex items-center gap-1">
                        <MapPin className="w-3 h-3" />
                        {kennel.city}
                      </p>
                    )}
                  </div>
                  <ChevronRight className="w-5 h-5 text-gray-500" />
                </Link>
              ))}
            </div>
            
            <div className="mt-6 text-center">
              <Link to={`/hodowle/${slug}`}>
                <Button className="bg-amber-500 hover:bg-amber-600 text-black font-medium rounded-full px-8">
                  Zobacz wszystkie hodowle {article.breed}
                </Button>
              </Link>
            </div>
          </motion.div>
        )}

        {/* Related catalog (for AI-generated articles with category+city) */}
        {relatedCatalog.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="mt-16 pt-8 border-t border-white/10"
            data-testid="related-catalog-section"
          >
            <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-2">
              <Briefcase className="w-6 h-6 text-emerald-400" />
              Sprawdź firmy w katalogu
            </h2>
            <div className="grid sm:grid-cols-2 gap-3">
              {relatedCatalog.map((c, i) => (
                <Link
                  key={i}
                  to={c.url}
                  className="flex items-center gap-3 p-4 bg-slate-800/50 rounded-xl border border-slate-700 hover:border-emerald-500/50 transition-colors group"
                  data-testid={`related-catalog-${c.type}`}
                >
                  <div className="w-10 h-10 rounded-lg bg-emerald-500/10 flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-5 h-5 text-emerald-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-white font-medium text-sm group-hover:text-emerald-400 transition-colors truncate">{c.label}</p>
                    <p className="text-gray-500 text-xs">{c.type === 'exact' ? 'Dokładne dopasowanie' : c.type === 'category' ? 'Wszystkie miasta' : 'Inne miasto'}</p>
                  </div>
                  <ChevronRight className="w-4 h-4 text-gray-500 group-hover:text-emerald-400 flex-shrink-0" />
                </Link>
              ))}
            </div>
          </motion.div>
        )}

        {/* CTA — differentiated by article type */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className={`mt-16 p-8 border rounded-2xl text-center ${article.breed
            ? 'bg-gradient-to-r from-amber-500/10 to-orange-500/10 border-amber-500/20'
            : 'bg-gradient-to-r from-emerald-500/10 to-blue-500/10 border-emerald-500/20'
          }`}
        >
          {article.breed ? (
            <>
              <h3 className="text-2xl font-bold text-white mb-3">
                Jesteś hodowcą {article.breed}?
              </h3>
              <p className="text-gray-400 mb-6">
                Dołącz do ApisAI i pokaż swoją hodowlę tysiącom potencjalnych klientów szukających szczeniąt.
              </p>
              <Link to="/rejestracja">
                <Button className="bg-amber-500 hover:bg-amber-600 text-black font-bold rounded-full px-8 py-6 text-lg">
                  Dodaj swoją hodowlę za darmo
                  <ChevronRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
            </>
          ) : (
            <>
              <h3 className="text-2xl font-bold text-white mb-3">
                Prowadzisz firmę? Dodaj się do katalogu
              </h3>
              <p className="text-gray-400 mb-6">
                Dotrzyj do klientów szukających usług w Twoim mieście. Bez rejestracji, bez opłat.
              </p>
              <Link to="/dodaj-firme">
                <Button className="bg-emerald-500 hover:bg-emerald-600 text-white font-bold rounded-full px-8 py-6 text-lg">
                  Dodaj firmę za darmo
                  <ChevronRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
            </>
          )}
        </motion.div>

        {/* Powiązane artykuły */}
        {relatedArticles.length > 0 && (
          <div className="mt-16 pt-8 border-t border-white/10">
            <h2 className="text-2xl font-bold text-white mb-6">Czytaj też</h2>
            <div className="grid md:grid-cols-2 gap-4">
              {relatedArticles.map(a => (
                <Link key={a.slug} to={`/blog/${a.slug}`} className="flex items-center gap-4 p-4 bg-slate-800/50 rounded-xl border border-slate-700 hover:border-amber-500/50 transition-colors group">
                  <div className="w-12 h-12 rounded-xl bg-amber-500/10 flex items-center justify-center flex-shrink-0">
                    <Dog className="w-6 h-6 text-amber-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-white text-sm group-hover:text-amber-400 transition-colors truncate">{a.title}</h3>
                    <p className="text-xs text-gray-500 mt-1">{a.breed}</p>
                  </div>
                  <ChevronRight className="w-4 h-4 text-gray-500 group-hover:text-amber-400 flex-shrink-0" />
                </Link>
              ))}
            </div>
          </div>
        )}
      </article>

      {/* Footer */}
      <footer className="py-8 border-t border-white/10">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <p className="text-gray-500 text-sm">
            © {new Date().getFullYear()} ApisAI — Platforma dla hodowców
          </p>
        </div>
      </footer>
    </div>
  );
}
